import React, { useEffect, useState, useRef } from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Input,
  InputGroup,
  InputRightElement,
  Button,
  Image,
  FormControl,
  FormLabel,
  Box,
  Text,
  Flex
} from '@chakra-ui/react';

import { styles, variantStyles } from "./styles";
import { inputDesign, activeLabelStyles } from "../styles/theme";

const DropdownInputDoctorList = ({
  dropDownPlace,
  options,
  variant,
  onClick,
  maxLength,
  onKeyDown,
  onKeyPress,
  id,
  defaultOption,
  inputStyleCss,
  readOnly = "readonly",
  isdisabled = false,
  nonClickable,
  isLabel = true,
  optionStyle,
  dropPlaceStyle,
  menuList,
  value,
  toggleDropdown,
  isOpen,
  dropdownContainerRef,
  arrowCSS,
  doctorData = [],
  isDoctorDropdown = false
}) => {
  const variantStyle = variantStyles[variant] || variantStyles.defaultSide;
  const [inputValue, setInputValue] = useState(value || "");
  const [showMenu, setShowMenu] = useState(false);
  const isLabelActive = inputValue !== "";
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (defaultOption) {
      setInputValue(defaultOption);
    }
  }, [defaultOption]);

  useEffect(() => {
    if (value !== undefined) {
      setInputValue(value);
    }
  }, [value]);

  // Handle external isOpen prop if provided
  useEffect(() => {
    if (isOpen !== undefined) {
      setShowMenu(isOpen);
    }
  }, [isOpen]);

  const handleBlur = (event) => {
    // Close menu on blur if not clicking on menu items
    setTimeout(() => {
      if (!menuRef.current?.contains(document.activeElement)) {
        setShowMenu(false);
        toggleDropdown?.();
      }
    }, 100);
  };

  // Helper function to format doctor display name with prefix
  const formatDoctorName = (doctor) => {
    if (!doctor) return "";

    // Only add "Dr." prefix if rolename is "Doctor"
    const shouldAddPrefix = doctor.rolename?.toLowerCase() === "doctor" && 
                           !doctor.fullname?.toLowerCase().includes('dr.');
    
    const prefix = shouldAddPrefix ? 'Dr. ' : '';
    
    return `${prefix}${doctor.fullname || ''}`;
  };

  // Helper function to get doctor specialties
  const getDoctorSpecialties = (doctor) => {
    if (!doctor || !doctor.doctorspeciality || doctor.doctorspeciality.length === 0) {
      return "";
    }
    
    const specialties = doctor.doctorspeciality.map(speciality => {
      if (speciality.specialityname === "Other" && speciality.othervalue) {
        return speciality.othervalue;
      }
      return speciality.specialityname;
    }).filter(specialty => specialty && specialty.trim() !== "");
    
    return specialties.join(", ");
  };

  // Helper function to get doctor role
  const getDoctorRole = (doctor) => {
    if (!doctor) return "";
    return doctor.rolename || "";
  };

  // Function to format the complete doctor display string for input field
  const formatDoctorDisplayString = (doctor) => {
    if (!doctor) return "";

    const displayName = formatDoctorName(doctor);
    const specialties = getDoctorSpecialties(doctor);
    const role = getDoctorRole(doctor);

    let displayString = displayName;

    // Add specialties in brackets if available
    if (specialties && specialties.trim() !== "") {
      displayString += ` [${specialties}]`;
    }

    // Add role if available
    if (role && role.trim() !== "") {
      displayString += ` ${role}`;
    }

    return displayString;
  };

  const handleOptionClick = (option) => {
    let displayValue = option;
    
    // If it's a doctor dropdown and we have doctor data, format the display value
    if (isDoctorDropdown && doctorData && doctorData.length > 0) {
      const doctor = doctorData.find(doc => doc.fullname === option);
      if (doctor) {
        displayValue = formatDoctorDisplayString(doctor);
      }
    }
    
    setInputValue(displayValue);
    onClick(option); // Pass the original option (doctor name) to parent
    setShowMenu(false);
    inputRef.current.focus();
  };

  const handleInputClick = () => {
    if (!nonClickable) {
      setShowMenu(prev => !prev); // Toggle menu state
    }
  };

  const handleArrowClick = (e) => {
    e.stopPropagation(); // Prevent event bubbling
    if (!nonClickable) {
      setShowMenu(prev => !prev); // Toggle menu state
    }
  };

  // Function to render menu items based on whether it's a doctor dropdown
  const renderMenuItems = () => {
    if (!isDoctorDropdown || !doctorData || doctorData.length === 0) {
      return options?.map((option, index) => (
        <MenuItem key={index} onClick={() => handleOptionClick(option)}>
          {option}
        </MenuItem>
      ));
    }

    return options?.map((option, index) => {
      const doctor = doctorData.find(doc => doc.fullname === option);
      if (!doctor) {
        return (
          <MenuItem key={index} onClick={() => handleOptionClick(option)}>
            {option}
          </MenuItem>
        );
      }

      const displayName = formatDoctorName(doctor);
      const specialties = getDoctorSpecialties(doctor);
      const role = getDoctorRole(doctor);

      return (
        <MenuItem key={index} onClick={() => handleOptionClick(option)} py="8px">
          <Box width="100%">
            <Flex direction="row" align="center" wrap="wrap" gap="8px">
              <Text fontWeight="600" fontSize="14px" minW="120px">
                {displayName}
              </Text>
              {specialties && (
                <Text fontSize="13px" color="gray.600" fontStyle="italic">
                  [{specialties}]
                </Text>
              )}
              {role && (
                <Text fontSize="13px" color="gray.500" ml={specialties ? "4px" : "0"}>
                  {role}
                </Text>
              )}
            </Flex>
          </Box>
        </MenuItem>
      );
    });
  };

  return (
    <Menu isOpen={showMenu} onClose={() => setShowMenu(false)} pos="relative">
      <FormControl sx={{ ...inputDesign.formStyles.container, ...inputStyleCss }} ref={dropdownContainerRef}>
        <InputGroup>
          <Input
            placeholder=" "
            value={inputValue}
            sx={{ ...styles, ...variantStyle, ...inputDesign.formStyles.input, ...optionStyle }}
            onChange={(e) => setInputValue(e.target.value)}
            readOnly={readOnly}
            cursor="pointer"
            onKeyDown={(e) => {
              onKeyDown && onKeyDown(e);
            }}
            onKeyPress={onKeyPress}
            id={id}
            maxLength={maxLength}
            p={isLabel ? "12px 10px 0 10px" : "12px 10px 10px 10px"}
            {...optionStyle}
            ref={inputRef}
            onBlur={handleBlur}
            onClick={handleInputClick}
          />
          <InputRightElement width="3rem">
            <MenuButton
              as={Button}
              h="1.75rem"
              bg="none"
              sx={{ top: "5px", _hover: { bg: "none" }, _active: { bg: "none" } }}
              size="sm"
              disabled={isdisabled}
              {...arrowCSS}
              onClick={handleArrowClick}
            >
              <Image w="15px" src="/assets/imgs/double-arrow.png" />
            </MenuButton>
          </InputRightElement>
        </InputGroup>
        {isLabel && (
          <FormLabel sx={{
            ...inputDesign.formStyles.label,
            ...(isLabelActive && activeLabelStyles),
            ...dropPlaceStyle
          }}>{dropDownPlace}</FormLabel>
        )}
      </FormControl>
      <MenuList 
        zIndex="999" 
        maxH="200px" 
        overflowY="auto" 
        ref={menuRef} 
        minW="400px" 
        {...menuList}
        onMouseDown={(e) => e.preventDefault()}
      >
        {renderMenuItems()}
      </MenuList>
    </Menu>
  );
};

export default DropdownInputDoctorList;